import { progressApi } from '@/api/progress';

/**
 * Отметка «запись не получилась» — в серверный журнал `[запись]` (решение владельца 13.09.2026).
 *
 * 🔴 ЗАЧЕМ. Живого iPhone в проверке нет, и что именно видят ученики, узнать
 * можно только с сервера. Вечером 12.09 отказанные файлы не оставили следа ни в
 * базе, ни в журнале progress: files-service отвергал их раньше
 * (техдолг п. 214). Теперь каждый вердикт «не получилось» на клиенте — строка
 * журнала с учеником, блоком, часами, причиной и устройством (`User-Agent`).
 *
 * ⚠️ ОТДЕЛЬНОЙ РУЧКИ У СЕРВЕРА НЕТ — ИДЁМ ТОЙ ЖЕ, ЧТО ОТВЕТ НА ЗАДАНИЕ. Вместо
 * ссылки на файл уходит `client:broken`. Для progress это «файла нет»: он пишет
 * строку `[запись]` и отказывает `422 empty_recording / no_file`, НИЧЕГО НЕ
 * СОХРАНЯЯ (договор `record-integrity.md` §2.2, проверка 1). Пустую строку слать
 * нельзя — обработчик отвергает её раньше службы, и строки в журнале не будет.
 *
 * ⚠️ МИМО ОЧЕРЕДИ ОТПРАВКИ И БЕЗ ПОВТОРОВ. Это отметка, а не ответ ученика:
 * отказ сервера здесь — ожидаемый исход, и повторять его или показывать ученику
 * нечего. Отметка никогда не бросает и не ждёт — вердикт на экране от неё не зависит.
 */

/** Что показал клиент: начало файла не опознано или черновик не играет. */
export type BrokenRecordingReason = 'client_nohead' | 'client_noplay';

/** Значение вместо ссылки на файл: не URL — значит, для сервера «файла нет». */
export const BROKEN_RECORDING_MARK = 'client:broken';

export function reportBrokenRecording(input: {
  blockId: string;
  reason: BrokenRecordingReason;
  /** Часы страницы; неизвестные и кривые не шлём — сервер их всё равно отбросил бы. */
  clientSeconds?: number | null;
}): void {
  const { blockId, reason, clientSeconds } = input;
  const seconds =
    typeof clientSeconds === 'number' && Number.isFinite(clientSeconds) && clientSeconds >= 0 && clientSeconds <= 7200
      ? Math.round(clientSeconds * 10) / 10
      : undefined;
  try {
    void progressApi
      .answerBlock(blockId, {
        answer_data: BROKEN_RECORDING_MARK,
        is_correct: false,
        block_type: 'audio_record',
        client_seconds: seconds,
        stop_reason: reason,
      })
      .catch(() => undefined);
  } catch {
    /* отметка не должна ронять блок записи */
  }
}
